import type { MaterialSrcOverride } from "building-types/materials/materialTypes";
import type { StairConstruction, Stringer } from "building-types/stairs/stairConstruction";
import {
  STAIR_DEFAULT_MATERIAL,
  StairBottomShape,
  StairConstructionType,
  StringerLayout,
  StringerPositioning,
  defaultConstruction,
} from "building-types/stairs/stairConstruction";
import type { StairExtrusion } from "building-types/stairs/stairTypes";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { TREAD_MIN_DEPTH } from "doc/elements/stairSystem/stairConstants";
import { faceNew } from "geom/brep";
import { DIST_EPSILON } from "geom/core";
import { extrudeFace } from "geom/extrude";
import { intersectRay2Ray2 } from "geom/intersect";
import { line2Normal, line2Vec } from "geom/line2";
import { planeCopyMoved } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { maxPolyAlignedToEdge, polyLine2Edge, polyLine2SetCW } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { Vec2 } from "geom/vec2";
import { vec2Normalize, vec2Scale } from "geom/vec2";
import {
  uvGenerationForStairProfile,
  uvGenerationForStairRiser,
  uvGenerationForStairStringer,
  uvGenerationForStairTread,
} from "../../utils/uvGenerationForEntities";
import {
  STAIR_COMPONENT_RISER,
  STAIR_COMPONENT_STAIR_BODY,
  STAIR_COMPONENT_STRINGER,
  STAIR_COMPONENT_TREAD,
} from "./stairExportComponents";

/**
 * Prevent meaningless stringer spacing values
 */
const STRINGER_MIN_MAX_SPACING = 0.125;

/**
 * construct stringer extrusions for a given stair block outline and given
 * construction system. This function only calculates stringers for assembled
 * stair construction types; monolithic stair stringers are considered full-width
 * extrusions, and are calculated in fullWidthProfiles
 */
export function stringers(
  outline: PolyLine2,
  startEdgeIndex: number,
  plane: Plane | undefined,
  treadPoints: number[],
  runBottomX: number,
  construction: StairConstruction | undefined,
): StairExtrusion[] {
  const extrusions: StairExtrusion[] = [];

  if (!construction || !plane) {
    return extrusions;
  }

  switch (construction.type) {
    case StairConstructionType.ASSEMBLED: {
      const [x1, y1, x2, y2] = polyLine2Edge(outline, startEdgeIndex);
      const [x2d, y2d] = [x2 - x1, y2 - y1];
      const [nx, ny] = line2Vec([x1, y1, x2, y2]);
      const [unx, uny] = vec2Normalize(nx, ny);

      const { maxWidth } = maxPolyAlignedToEdge(outline, startEdgeIndex);

      const {
        stairBottomOffset = 0,
        stringers = [],
        stringerLayout,
        stringerSideOffset = 0,
        stringerThickness = 1.75,
        riserThickness = 0,
        treadThickness = 0,
        stringerMinDepth = 0,
        stringerMaterial = STAIR_DEFAULT_MATERIAL,
        stringerTopExtension = 0,
      } = construction;

      let { stringerMaxSpacing = 24 } = construction;

      if (stringerMaxSpacing < STRINGER_MIN_MAX_SPACING) {
        stringerMaxSpacing = STRINGER_MIN_MAX_SPACING;
      }

      let resolvedStringers: Stringer[] = [];
      switch (stringerLayout) {
        case StringerLayout.MANUAL:
          resolvedStringers = stringers;
          break;
        case StringerLayout.AUTOMATIC:
          const fillWidth = maxWidth - 2 * stringerSideOffset - stringerThickness;
          const numFillStringers = Math.ceil(fillWidth / stringerMaxSpacing);
          resolvedStringers = [
            {
              positioning: StringerPositioning.FROM_LEFT,
              position: stringerSideOffset,
              width: stringerThickness,
              closed: false,
            },
            {
              positioning: StringerPositioning.FROM_RIGHT,
              position: stringerSideOffset,
              width: stringerThickness,
              closed: false,
            },
          ];
          for (let s = 0; s < numFillStringers; s++) {
            resolvedStringers.push({
              positioning: StringerPositioning.FROM_LEFT,
              position: stringerSideOffset + fillWidth * (s / numFillStringers),
              width: stringerThickness,
              closed: false,
            });
          }
          break;
      }

      const hasRiser = construction.hasRisers && !!riserThickness;

      const riserShelf = stairBottomOffset > 0;

      const adjustedTreads = adjustTreadPoints({
        treadPoints,
        runBottomX,
        riserThickness: hasRiser ? riserThickness : 0,
        treadThickness,
        riserShelf,
        stairBottomOffset,
      });

      const { profile } = makeStringerProfile({
        treadPoints: adjustedTreads,
        runBottomX,
        stairBottomOffset,
        stringerMinDepth,
        riserShelf,
        stringerTopExtension,
      });

      // TODO: this needs to use normalized param inputs
      for (const { positioning, position, width } of resolvedStringers) {
        let pt: [number, number];
        switch (positioning) {
          case StringerPositioning.FROM_RIGHT: {
            const dist = position + width;
            pt = [x2d - unx * dist, y2d - uny * dist];
            break;
          }
          case StringerPositioning.FROM_LEFT: {
            const dist = position;
            pt = [unx * dist, uny * dist];
            break;
          }
          case StringerPositioning.PERCENTAGE: {
            const dist = position * maxWidth - width / 2;
            pt = [unx * dist, uny * dist];
            break;
          }
        }

        const newPlane = planeCopyMoved(plane, pt[0], pt[1], 0);

        const ext = {
          extrusion: extrudeFace(faceNew([new Float64Array(profile)], newPlane), unx * width, uny * width, 0),
          material: {
            materialSrc: stringerMaterial,
            uvGen: uvGenerationForStairStringer(),
            exportComponentName: STAIR_COMPONENT_STRINGER,
          },
        };

        extrusions.push(ext);
      }
    }
    case StairConstructionType.MONOLITHIC:
    // monolithic stringers are generated in fullWidthProfiles
    default: {
    }
  }

  return extrusions;
}

interface TreadPointsConfig {
  runBottomX: number;
  treadPoints: number[];
  riserThickness: number;
  treadThickness: number;
  riserShelf?: boolean;
  stairBottomOffset?: number;
}

/**
 * adjust set of tread points to take riser
 * and tread thicknesses into account
 * ```
 *                          . .         .
 *                o- - - - -o .      .
 *                . O---------O   .
 *      ->        . .          .
 *  | o- - - - - -o .       .
 *  V . O-----------O    .
 *    . .             .
 *    +-+ riser shelf (if stringer continues below)
 *    .         .
 *    .      .
 *    .   .
 *    .
 *
 * ```
 */
export function adjustTreadPoints({
  treadPoints,
  runBottomX,
  riserThickness,
  treadThickness,
  riserShelf = false,
  stairBottomOffset = 0,
}: TreadPointsConfig): number[] {
  const len = treadPoints.length;

  const adjusted: number[] = [];

  riserShelf && adjusted.push(runBottomX, treadPoints[1], runBottomX, treadPoints[1] + riserThickness);

  for (let i = 0; i < len; i += 4) {
    const [noseX, noseY, innerX, innerY] = [treadPoints[i], treadPoints[i + 1], treadPoints[i + 2], treadPoints[i + 3]];

    const treadDepth = innerX - noseX;

    // the last tread should not be adjusted for riser thickness
    if (noseX - treadThickness > runBottomX + stairBottomOffset) {
      if (i + 4 >= len || treadDepth >= riserThickness) {
        adjusted.push(noseX - treadThickness, noseY + riserThickness, innerX - treadThickness, innerY);
      } else {
        adjusted.push(noseX - treadThickness, noseY + riserThickness, innerX - treadThickness, innerY + riserThickness);
      }
    }
  }
  return adjusted;
}

interface StringerProfileConfig {
  treadPoints: number[];
  runBottomX?: number;
  stairBottomOffset?: number;
  stringerMinDepth?: number;
  stringerTopExtension?: number;
  riserShelf?: boolean;
}

export function projectToStringerUnderside(
  [bottomX, bottomY]: Vec2,
  [topX, topY]: Vec2,
  stringerMinDepth: number,
): { bottom: Vec2; top: Vec2 } {
  const [normX, normY] = line2Normal(bottomX, bottomY, topX, topY);
  const [distX, distY] = vec2Scale(normX, normY, stringerMinDepth);

  return {
    bottom: [bottomX - distX, bottomY - distY],
    top: [topX - distX, topY - distY],
  };
}

/**
 * given a set of tread points and config, calculate which points describe the
 * nose and inside points of the top and bottom treads
 */
export function noseInnerTreadPoints({
  treadPoints,
  riserShelf = false,
  runBottomX = 0,
  stairBottomOffset = 0,
}: StringerProfileConfig): {
  bottomNose: Vec2;
  bottomInner: Vec2;
  topNose: Vec2;
  topInner: Vec2;
} {
  const len = treadPoints.length;

  if (!(len > 3)) {
    console.assert(false, "need at least two points to calculate treads");
  }

  // the "riser shelf," a cut into the stringer
  // where the riser fits, it is represented using
  // the first set of tread points, and should be
  // skipped
  const idxOffset = riserShelf ? 4 : 0;

  const bottom = runBottomX + stairBottomOffset;

  const firstNoseX = treadPoints[idxOffset + 0];
  const firstNoseY = treadPoints[idxOffset + 1];
  let firstInnerX: number;
  let firstInnerY: number;

  let secondInnerX: number;
  let secondInnerY: number;

  const lastInnerX = treadPoints[len - 2];
  const lastInnerY = treadPoints[len - 1];

  // if there's only one stair, the inner point of
  // the first tread and the inner point of the last
  // tread will be the same, so use the bottom of the
  // stair at the first tread's nose as the lower
  // point
  if (len === 4) {
    firstInnerX = bottom;
    firstInnerY = firstNoseY;
    secondInnerX = lastInnerX;
    secondInnerY = lastInnerY;
  } else {
    firstInnerX = treadPoints[idxOffset + 2];
    firstInnerY = treadPoints[idxOffset + 3];
    secondInnerX = treadPoints[idxOffset + 6];
    secondInnerY = treadPoints[idxOffset + 7];
  }

  return {
    bottomNose: [firstNoseX, firstNoseY],
    bottomInner: [firstInnerX, firstInnerY],
    topNose: [lastInnerX, lastInnerY],
    topInner: [secondInnerX, secondInnerY],
  };
}

export interface StringerProfile {
  profile: PolyLine2;
  backSlopeLower?: Vec2;
  backSlopeUpper?: Vec2;
}

export function makeStringerProfile(config: StringerProfileConfig): StringerProfile {
  const { treadPoints, runBottomX = 0, stairBottomOffset = 0, stringerMinDepth = 0 } = config;
  let { stringerTopExtension = 0 } = config;

  if (stringerTopExtension < 0) {
    stringerTopExtension = 0;
  }

  if (!treadPoints) {
    return { profile: new Float64Array() };
  }

  const len = treadPoints.length;

  if (len < 4) {
    return { profile: new Float64Array() };
  }

  let bottom = runBottomX + stairBottomOffset;

  const firstLastTreadPoints = noseInnerTreadPoints(config);

  const {
    bottomInner: firstInner,
    topInner: lastInner,
    bottomNose: [firstNoseX, firstNoseY],
    topNose: [lastInnerX],
  } = firstLastTreadPoints;

  let {
    topNose: [, lastInnerY],
  } = firstLastTreadPoints;

  const {
    bottom: [bx, by],
    top: [tx, ty],
  } = projectToStringerUnderside(firstInner, lastInner, stringerMinDepth);

  // ray representing the back slope face
  const rSlopeOrigX = bx;
  const rSlopeOrigY = by;
  const rSlopeDirX = tx - bx;
  const rSlopeDirY = ty - by;

  // intersect the back slope with a ray going straight back
  // from the bottom of the first riser
  const slopeBottomIntersection = intersectRay2Ray2(
    bottom,
    firstNoseY,
    0,
    1,
    rSlopeOrigX,
    rSlopeOrigY,
    rSlopeDirX,
    rSlopeDirY,
  );
  let bottomX: number;
  let bottomY: number;
  if (!slopeBottomIntersection) {
    return { profile: new Float64Array() };
  }
  ({ x: bottomX, y: bottomY } = slopeBottomIntersection);

  // if the intersection of the back slope and bottom of first riser is in
  // *front* of the first nose, re-intersect the back slope with a ray going
  // straight down from the first nose
  if (Math.abs(bottomY - firstNoseY) > DIST_EPSILON && bottomY <= firstNoseY) {
    const slopeFrontIntersection = intersectRay2Ray2(
      firstNoseX,
      firstNoseY,
      -1,
      0,
      rSlopeOrigX,
      rSlopeOrigY,
      rSlopeDirX,
      rSlopeDirY,
    );
    if (!slopeFrontIntersection) {
      return { profile: new Float64Array() };
    }
    ({ x: bottomX, y: bottomY } = slopeFrontIntersection);
    bottom = bottomX;
  }

  // intersect the back slope with a ray going straight down
  // from the last tread inner point
  const slopeBackIntersection = intersectRay2Ray2(
    lastInnerX,
    lastInnerY + stringerTopExtension,
    -1,
    0,
    rSlopeOrigX,
    rSlopeOrigY,
    rSlopeDirX,
    rSlopeDirY,
  );
  if (!slopeBackIntersection) {
    return { profile: new Float64Array() };
  }
  let { x: topX, y: topY } = slopeBackIntersection;

  // if the intersection of the last tread inner and the back slope
  // is greater than the top of the tread, find the point where
  // they cross: this is the end of the stringer
  if (topX > lastInnerX) {
    const topSlopeIntersection = intersectRay2Ray2(
      lastInnerX,
      lastInnerY + stringerTopExtension,
      0,
      1,
      rSlopeOrigX,
      rSlopeOrigY,
      rSlopeDirX,
      rSlopeDirY,
    );
    assertIsNotNullOrUndefined(topSlopeIntersection);
    ({ x: topX, y: topY } = topSlopeIntersection);
  } else {
    // ... otherwise replace the last tread point with the intersection
    treadPoints[treadPoints.length - 1] = topY;
    lastInnerY = topY;
  }

  // the bottom of the run is resolved
  const profile = [bottom, firstNoseY, ...treadPoints];

  let backSlopeLower: Vec2 | undefined;
  let backSlopeUpper: Vec2 | undefined;

  if (Math.abs(topX - bottom) > DIST_EPSILON && topX <= bottom) {
    if (Math.abs(lastInnerY - firstNoseY) > DIST_EPSILON) {
      // the intersection of the back slope of the stringer is lower than
      // the bottom of the stair, indicating that there should be no back
      // slope at all; make a flat bottom
      profile.push(bottom, lastInnerY);
    }
  } else {
    if (Math.abs(topX - lastInnerX) > DIST_EPSILON || Math.abs(topY - lastInnerY) > DIST_EPSILON) {
      // expected case, where the top point != the last inner tread point,
      // add the vertex normally
      profile.push(topX, topY);
      backSlopeUpper = [topX, topY];
    }

    if (
      Math.abs(bottomY - topY) > DIST_EPSILON &&
      (Math.abs(bottomX - bottom) > DIST_EPSILON || Math.abs(bottomY - firstNoseY) > DIST_EPSILON)
    ) {
      // expected case, where the bottom point != the first nose point
      // add the vertex normally
      profile.push(bottomX, bottomY);
    }

    // whatever the case with respect to intersections, this is either
    // the bottom heel of the stringer or the lowest point of the stair
    backSlopeLower = [bottomX, bottomY];
  }

  profile.push(bottom, firstNoseY); // close the loop

  return {
    profile: polyLine2SetCW(Float64Array.from(profile)),
    backSlopeLower,
    backSlopeUpper,
  };
}

export interface StairProfile {
  readonly profile: PolyLine2;
  readonly material: MaterialSrcOverride;
}

/**
 * Profiles for elements that extend the full width of the
 * stair run.
 * ```
 * treadPoints:
 *
 *              nose
 *              +------+
 *       nose   |      inner
 *       +------+
 *       :      inner
 *   --- x --- - --- - --- runBottomX (z in 3d)
 * ```
 */
export function fullWidthProfiles(
  treadPoints: number[],
  runBottomX: number,
  construction: StairConstruction = defaultConstruction,
): StairProfile[] {
  const profiles: StairProfile[] = [];

  const len = treadPoints.length;

  const firstNoseY = treadPoints[1];
  const lastInnerY = treadPoints[len - 1];

  switch (construction.type) {
    case StairConstructionType.MONOLITHIC: {
      const {
        stairBottomShape,
        stairBottomOffset = 0,
        stringerTopExtension = 0,
        stringerMinDepth,
        stairMaterial = STAIR_DEFAULT_MATERIAL,
        hasTreadMaterial,
        treadThickness = TREAD_MIN_DEPTH,
      } = construction;
      const bottom = runBottomX + stairBottomOffset;

      if (hasTreadMaterial) {
        profiles.push(...makeTreadAndRiserProfiles({ treadPoints, runBottomX, construction }));
        treadPoints = adjustTreadPoints({
          treadPoints,
          runBottomX,
          stairBottomOffset,
          riserThickness: 0,
          riserShelf: false,
          treadThickness,
        });
      }

      let profile: PolyLine2;
      switch (stairBottomShape) {
        default:
        case StairBottomShape.FLAT: {
          profile = polyLine2SetCW(
            Float64Array.from([bottom, firstNoseY, ...treadPoints, bottom, lastInnerY, bottom, firstNoseY]),
          );
          break;
        }
        case StairBottomShape.SLOPED: {
          ({ profile } = makeStringerProfile({
            treadPoints,
            runBottomX,
            stairBottomOffset,
            stringerMinDepth,
            stringerTopExtension,
          }));
          break;
        }
      }

      if (!profile) {
        break;
      }

      profiles.push({
        profile,
        material: {
          materialSrc: stairMaterial,
          uvGen: uvGenerationForStairProfile(),
          exportComponentName: STAIR_COMPONENT_STAIR_BODY,
        },
      });

      break;
    }
    case StairConstructionType.ASSEMBLED: {
      profiles.push(...makeTreadAndRiserProfiles({ treadPoints, runBottomX, construction }));
      break;
    }
    default: {
    }
  }
  return profiles;
}

interface TreadRiserProps {
  treadPoints: number[];
  runBottomX: number;
  construction: StairConstruction;
}

function makeTreadAndRiserProfiles({
  treadPoints,
  runBottomX,
  construction: {
    type,
    treadThickness = TREAD_MIN_DEPTH,
    treadMaterial = STAIR_DEFAULT_MATERIAL,
    hasRisers,
    riserThickness,
    nosing = 0,
    riserMaterial = STAIR_DEFAULT_MATERIAL,
  },
}: TreadRiserProps): StairProfile[] {
  const profiles: StairProfile[] = [];
  const len = treadPoints.length;
  let prevNoseX = runBottomX;
  for (let i = 0; i < len; i += 4) {
    const noseX = treadPoints[i];
    const noseY = treadPoints[i + 1];
    const innerX = treadPoints[i + 2];
    const innerY = treadPoints[i + 3];
    // add tread profile
    profiles.push({
      profile: polyLine2SetCW(
        Float64Array.from([
          noseX,
          noseY - nosing,
          innerX,
          innerY,
          innerX - treadThickness,
          innerY,
          noseX - treadThickness,
          noseY - nosing,
          noseX,
          noseY - nosing,
        ]),
      ),
      material: {
        materialSrc: treadMaterial,
        uvGen: uvGenerationForStairTread(),
        exportComponentName: STAIR_COMPONENT_TREAD,
      },
    });
    // add riser profile if greater than 0
    type === StairConstructionType.ASSEMBLED &&
      hasRisers &&
      riserThickness &&
      profiles.push({
        profile: polyLine2SetCW(
          Float64Array.from([
            noseX - treadThickness,
            noseY,
            noseX - treadThickness,
            noseY + riserThickness,
            prevNoseX - treadThickness,
            noseY + riserThickness,
            prevNoseX - treadThickness,
            noseY,
            noseX - treadThickness,
            noseY,
          ]),
        ),
        material: {
          materialSrc: riserMaterial,
          uvGen: uvGenerationForStairRiser(),
          exportComponentName: STAIR_COMPONENT_RISER,
        },
      });
    prevNoseX = noseX;
  }
  return profiles;
}
